"use client";

import { useState, useEffect } from "react";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
} from "@/store/modules/paymentMethod/thunks";
import { clearPaymentMethodError } from "@/store/modules/paymentMethod/slice";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { paymentMethodSchema } from "@/validations";
import { usePermission } from "@/hooks/usePermission/usePermission";
import { useAuth } from "@/context/AuthContext";

const usePaymentMethod = (onSuccess?: () => void) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { hasPermission, loading: loadingPermission } = usePermission();
  const { establishment, baseURL } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(paymentMethodSchema),
    defaultValues: {
      name: "",
      percentage_fee: 0,
    },
  });

  const setFormData = (
    data: Partial<typeof paymentMethodSchema.__outputType>,
  ) => {
    reset(data);
  };

  const onSubmit = async (data: typeof paymentMethodSchema.__outputType) => {
    try {
      let result;

      // Limpa erro anterior antes de tentar salvar novamente
      dispatch(clearPaymentMethodError());

      // Normaliza entrada: aceita vírgula como separador decimal e remove '%' se presente
      const rawPercentage = (data as any)?.percentage_fee ?? 0;
      const rawString = String(rawPercentage)
        .replace("%", "")
        .replace(",", ".")
        .trim();
      const parsed = Number(rawString);

      // Mantém o payload dentro do intervalo 0..100 para não disparar validação do backend.
      let normalized = Number.isFinite(parsed) ? parsed : 0;
      if (normalized < 0) normalized = 0;
      if (normalized > 100) normalized = 100;

      const payload = {
        ...data,
        percentage_fee: Number(normalized.toFixed(2)),
      };

      if (paymentMethodId && hasPermission("update payment method")) {
        result = await dispatch(
          updatePaymentMethod({ id: paymentMethodId, ...payload }),
        );
      } else {
        if (hasPermission("create payment method")) {
          result = await dispatch(createPaymentMethod(payload));
        }
      }

      if (
        createPaymentMethod.fulfilled.match(result) ||
        updatePaymentMethod.fulfilled.match(result)
      ) {
        if (onSuccess) onSuccess();
        reset();
        setPaymentMethodId(null);
      }
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
    }
  };

  const { paymentMethods, loading, error } = useSelector(
    (state: any) => state.paymentMethods,
  );
  const dispatch: AppDispatch = useDispatch();

  const [paymentMethodId, setPaymentMethodId] = useState<any>();
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    // Aguarda establishment e baseURL estarem prontos antes de fazer requests
    if (!baseURL || !establishment) return;

    // Só carrega se não tem permissão, não carregou ainda, não está carregando E não tem dados
    const shouldLoad =
      hasPermission("read payment method") &&
      !hasLoaded &&
      !loading &&
      (!paymentMethods?.data || paymentMethods.data.length === 0);

    if (shouldLoad) {
      dispatch(fetchPaymentMethods(params));
      setHasLoaded(true);
    } else if (paymentMethods?.data && paymentMethods.data.length > 0) {
      // Se já tem dados (vindos de outro hook), marca como carregado
      setHasLoaded(true);
    }
  }, [
    hasPermission,
    hasLoaded,
    loading,
    paymentMethods?.data?.length,
    baseURL,
    establishment,
    dispatch,
    params,
  ]);

  const totalItems = paymentMethods?.total ?? 0;
  const itemsPerPage = paymentMethods?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(
    paymentMethods?.current_page ?? 1,
  );

  useEffect(() => {
    if (
      paymentMethods?.current_page &&
      paymentMethods.current_page !== currentPage
    ) {
      setCurrentPage(paymentMethods.current_page);
    }
  }, [paymentMethods?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    updateParams({
      search: value,
      current_page: 1, // reset pagina p/ 1 ao filtrar
    });
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    try {
      if (paymentMethodId && hasPermission("delete payment method")) {
        await dispatch(deletePaymentMethod(paymentMethodId));
        setPaymentMethodId(null);

        return true;
      }
    } catch (error) {
      console.error("Error deleting payment method:", error);
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting,
    paymentMethods,
    loading,
    error,
    setPaymentMethodId,
    handleDelete,
    setFormData,
    handlePage,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    handleFilterChange,
    control,
    clearError: () => dispatch(clearPaymentMethodError()),
  };
};

export default usePaymentMethod;
